import { FC, useCallback, MouseEvent } from 'react';
import { useFragment } from 'react-relay';
import { isEmpty } from 'lodash';
import { OutputData } from '@editorjs/editorjs';
import graphql from 'babel-plugin-relay/macro';

import IconButton from '@mui/material/IconButton';
import Badge from '@mui/material/Badge';

import { EditorLanguageButton_language$key } from '__generated__/EditorLanguageButton_language.graphql';

import { useHoverable } from 'lib';
import { useEditor } from 'components/EditorJsField';

import {
  EditorLanguageButton_text$data,
  EditorLanguageButton_text$key,
  Locale,
} from '__generated__/EditorLanguageButton_text.graphql';
import Tooltip from '@mui/material/Tooltip';
import LanguageIndicator from 'components/LanguageIndicator';

type Props = {
  language: EditorLanguageButton_language$key;
  text: EditorLanguageButton_text$key | null;
};

const hasTranslation = (
  locale: Locale,
  text: EditorLanguageButton_text$data | null
) => {
  const translation = text?.blocks?.edges.find(
    block => locale === block.node.locale
  );

  const data = translation?.node.data
    ? (JSON.parse(translation.node.data) as OutputData)
    : null;

  return Boolean(translation && !isEmpty(data?.blocks));
};

const EditorLanguageButton: FC<Props> = props => {
  const { hovering, ...hoverable } = useHoverable();
  const { selectedLocale, setLocale } = useEditor();

  const language = useFragment(
    graphql`
      fragment EditorLanguageButton_language on Language {
        name
        locale

        ...LanguageIndicator_language
      }
    `,
    props.language
  );

  const text = useFragment(
    graphql`
      fragment EditorLanguageButton_text on Text {
        blocks {
          edges {
            node {
              locale
              data
            }
          }
        }
      }
    `,
    props.text
  );

  const { name, locale } = language;

  const handleClick = useCallback(
    (event: MouseEvent) => {
      event.stopPropagation();

      setLocale(locale);
    },
    [locale, setLocale]
  );

  return (
    <Tooltip title={name}>
      <IconButton size="small" onClick={handleClick} {...hoverable}>
        <Badge
          color="error"
          overlap="circular"
          variant="dot"
          invisible={!hasTranslation(locale, text)}>
          <LanguageIndicator
            sx={[
              { width: 24, height: 24 },
              !hovering &&
                selectedLocale !== locale && { filter: 'opacity(0.3)' },
            ]}
            language={language}
          />
        </Badge>
      </IconButton>
    </Tooltip>
  );
};

export default EditorLanguageButton;
